// Simple API client using fetch
const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'https://africa-space-agency-api.onrender.com/api/v1/'
const API_KEY = import.meta.env.VITE_API_KEY || ''

// Auth token management
let authToken: string | null = null

export function getAuthToken(): string | null {
  // Always read from sessionStorage to ensure we have the latest token
  authToken = sessionStorage.getItem('auth_token')
  return authToken
}

export function setAuthToken(token: string): void {
  authToken = token
  sessionStorage.setItem('auth_token', token)
}

export function clearAuthToken(): void {
  authToken = null
  sessionStorage.removeItem('auth_token')
}

// Generic fetch wrapper
export async function apiRequest<T>(
  endpoint: string,
  options: RequestInit = {}
): Promise<T> {
  const url = `${API_BASE_URL}${endpoint}`

  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    'X-Api-Key': API_KEY,
    ...(options.headers as Record<string, string>),
  }

  // Add auth token if available
  const token = getAuthToken()
  if (token) {
    headers.Authorization = `Bearer ${token}`
  }

  const config: RequestInit = {
    ...options,
    headers,
  }

  const response = await fetch(url, config)

  if (!response.ok) {
    if (response.status === 401) {
      clearAuthToken()
      throw new Error('Unauthorized')
    }
    throw new Error(`API Error: ${response.status} ${response.statusText}`)
  }

  return response.json()
}

// HTTP method helpers
export async function get<T>(endpoint: string): Promise<T> {
  return apiRequest<T>(endpoint, { method: 'GET' })
}

export async function post<T>(endpoint: string, data?: unknown): Promise<T> {
  return apiRequest<T>(endpoint, {
    method: 'POST',
    body: data ? JSON.stringify(data) : undefined,
  })
}

export async function put<T>(endpoint: string, data?: unknown): Promise<T> {
  return apiRequest<T>(endpoint, {
    method: 'PUT',
    body: data ? JSON.stringify(data) : undefined,
  })
}

export async function patch<T>(endpoint: string, data?: unknown): Promise<T> {
  return apiRequest<T>(endpoint, {
    method: 'PATCH',
    body: data ? JSON.stringify(data) : undefined,
  })
}

export async function del<T>(endpoint: string): Promise<T> {
  return apiRequest<T>(endpoint, { method: 'DELETE' })
}


export async function delFormData<T>(endpoint: string, formData: FormData): Promise<T> {
  const url = `${API_BASE_URL}${endpoint}`

  const headers: Record<string, string> = {
    'X-Api-Key': API_KEY,
  }

  // Add auth token if available
  const token = getAuthToken()
  if (token) {
    headers.Authorization = `Bearer ${token}`
  }

  const response = await fetch(url, {
    method: 'DELETE',
    headers,
    body: formData
  })

  if (!response.ok) {
    const errorText = await response.text()

    if (response.status === 401) {
      clearAuthToken()
      throw new Error('Unauthorized')
    }
    if (response.status === 403) {
      throw new Error(`Forbidden: ${errorText || response.statusText}`)
    }
    throw new Error(`API Error: ${response.status} ${response.statusText}`)
  }

  return response.json()
}

export async function postFormData<T>(endpoint: string, formData: FormData): Promise<T> {
  const url = `${API_BASE_URL}${endpoint}`

  const headers: Record<string, string> = {
    'X-Api-Key': API_KEY,
    // Note: Don't set Content-Type header - browser will set it with boundary
  }

  // Add auth token if available
  const token = getAuthToken()
  if (token) {
    headers.Authorization = `Bearer ${token}`
  }

  const response = await fetch(url, {
    method: 'POST',
    headers,
    body: formData
  })

  if (!response.ok) {
    const errorText = await response.text()

    if (response.status === 401) {
    
      throw new Error('Unauthorized')
    }
    if (response.status === 403) {
      throw new Error(`Forbidden: ${errorText || response.statusText}`)
    }
    throw new Error(`API Error: ${response.status} ${response.statusText}`)
  }

  return response.json()
}

// Multipart form data request for PUT (for file uploads)
export async function putFormData<T>(endpoint: string, formData: FormData): Promise<T> {
  const url = `${API_BASE_URL}${endpoint}`

  const headers: Record<string, string> = {
    'X-Api-Key': API_KEY,
    // Note: Don't set Content-Type header - browser will set it with boundary
  }

  // Add auth token if available
  const token = getAuthToken()
  if (token) {
    headers.Authorization = `Bearer ${token}`
  }

  const response = await fetch(url, {
    method: 'PUT',
    headers,
    body: formData
  })

  if (!response.ok) {
    const errorText = await response.text()

    if (response.status === 401) {
      clearAuthToken()
      throw new Error('Unauthorized')
    }
    if (response.status === 403) {
      throw new Error(`Forbidden: ${errorText || response.statusText}`)
    }
    throw new Error(`API Error: ${response.status} ${response.statusText}`)
  }

  return response.json()
}